import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/config';
// arsenal-tpms/frontend/src/components/vehicles/detail/AlarmProfilesTab.jsx
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    useTheme,
    alpha,
    useMediaQuery
} from '@mui/material';
import {
    Edit as EditIcon,
    Unarchive as UnassignIcon,
    Assignment as AssignIcon,
    Warning as AlarmIcon
} from '@mui/icons-material';
import { useApiCall, useMutation } from '../../../hooks/useApi';
import { api } from '../../../services/api';
import { useNotification } from '../../common/GlobalNotifications';
import { useAuth } from '../../../hooks/useAuth';

const safeFormatDateTime = (value) => {
    try {
        if (!value) return 'N/A';
        const date = new Date(value);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        return 'Error';
    }
};

const AlarmProfilesTab = ({ vehicle, t, onRefresh }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showSuccess, showError } = useNotification();
    const { hasPermission } = useAuth();

    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState('');

    const {
        data: alarmProfiles,
        loading: profilesLoading
    } = useApiCall(() => api.alarmProfiles.getAll(), []);

    const {
        data: assignments,
        loading: assignmentsLoading,
        refetch: refetchAssignments
    } = useApiCall(() =>
        vehicle?.id ? api.alarmProfiles.getAssignments({ vehicle_id: vehicle.id, active_only: true }) : null,
        [vehicle?.id]);

    const { mutate: assignProfile, loading: assigning } = useMutation(
        (data) => api.alarmProfiles.assignToVehicle(data),
        {
            onSuccess: () => {
                showSuccess(t('alarms.profiles.assignmentCreated', 'Perfil asignado exitosamente'));
                refetchAssignments();
                setAssignDialogOpen(false);
                setSelectedProfile('');
                onRefresh && onRefresh();
            },
            onError: (error) => showError(error.message || t('hardcoded.components_vehicles_detail_alarmprofilestab.error_al_asignar_perfil'))
        }
    );

    const { mutate: unassignProfile, loading: unassigning } = useMutation(
        (assignmentId) => api.alarmProfiles.removeAssignment(assignmentId),
        {
            onSuccess: () => {
                showSuccess(t('alarms.profiles.assignmentDeleted', 'Perfil desasignado exitosamente'));
                refetchAssignments();
                onRefresh && onRefresh();
            },
            onError: (error) => showError(error.message || t('hardcoded.components_vehicles_detail_alarmprofilestab.error_al_desasignar_perfil'))
        }
    );

    const vehicleAssignments = assignments?.filter(a =>
        String(a.vehicle_id) === String(vehicle?.id) && a.active
    ) || [];

    const availableProfiles = alarmProfiles?.filter(p =>
        p.active &&
        !p.is_template &&
        p.axle_count === vehicle?.axle_count &&
        !vehicleAssignments.some(va => String(va.profile_id) === String(p.id))
    ) || [];

    const handleAssignProfile = () => {
        if (!selectedProfile || !vehicle?.id) return;

        assignProfile({
            vehicle_id: vehicle.id,
            profile_id: selectedProfile,
            active: true
        });
    };

    if (profilesLoading || assignmentsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Typography variant="h6" fontWeight={700}>
                    {t('alarms.profiles.title', 'Perfiles de Alerta')}
                </Typography>
                {hasPermission('manager') && availableProfiles.length > 0 && (
                    <Button
                        variant="contained"
                        startIcon={<AssignIcon />}
                        onClick={() => setAssignDialogOpen(true)}
                        size={isMobile ? "small" : "medium"}
                    >
                        {t('alarms.profiles.assignProfile', 'Asignar Perfil')}
                    </Button>
                )}
            </Box>

            {vehicleAssignments.length > 0 ? (
                <Stack spacing={2} sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {vehicleAssignments.map((assignment) => {
                        const profile = alarmProfiles?.find(p => String(p.id) === String(assignment.profile_id));

                        if (!profile) return null;

                        return (
                            <Card
                                key={assignment.id}
                                sx={{
                                    border: 2,
                                    borderColor: 'primary.main',
                                    boxShadow: theme.shadows[3]
                                }}
                            >
                                <CardContent>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        mb: 2,
                                        flexWrap: 'wrap',
                                        gap: 1
                                    }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="h6" fontWeight={700} gutterBottom noWrap>
                                                {profile.name}
                                            </Typography>
                                            {profile.description && (
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    {profile.description}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                                <Chip
                                                    label={t('hardcoded.components_alarmprofiles_assignmentdialog.axle_count_ejes', { axle_count: profile.axle_count })}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={profile.applies_to_all_axles ? t('hardcoded.components_alarmprofiles_assignmentstable.todos') : t('hardcoded.components_alarmprofiles_profilecard.por_eje')}
                                                    size="small"
                                                />
                                                {profile.metrics_configured?.map(metric => (
                                                    <Chip
                                                        key={metric}
                                                        label={metric}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        {hasPermission('manager') && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title={t('common.edit', 'Editar')}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            window.location.href = `/alarm-profiles?edit=${profile.id}`;
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('alarms.profiles.removeAssignment', 'Desasignar')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => unassignProfile(assignment.id)}
                                                        disabled={unassigning}
                                                    >
                                                        <UnassignIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        {t('alarms.profiles.thresholdConfiguration', 'Configuración de umbrales')}
                                    </Typography>

                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                                    {profile.applies_to_all_axles ? t('hardcoded.components_alarms_alarmlist.metrica') : t('vehicleDetail.alarms.axle')}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                                    {t('hardcoded.components_alarmprofiles_profilecard.presion')}</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                                    {i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {profile.applies_to_all_axles ? (
                                                <TableRow>
                                                    <TableCell>{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const psiConfig = profile.configurations?.find(
                                                                c => c.metric_type === 'PSI' && c.axle_number === 1
                                                            );
                                                            if (!psiConfig) return '—';
                                                            const min = psiConfig.threshold_min;
                                                            const max = psiConfig.threshold_max;
                                                            let range = '';
                                                            if (min != null && max != null) range = `${min}–${max}`;
                                                            else if (min != null) range = `≥${min}`;
                                                            else if (max != null) range = `≤${max}`;
                                                            else range = '—';
                                                            return `${range} ${psiConfig.unit}`;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const tempConfig = profile.configurations?.find(
                                                                c => c.metric_type === 'TEMP' && c.axle_number === 1
                                                            );
                                                            if (!tempConfig) return '—';
                                                            const min = tempConfig.threshold_min;
                                                            const max = tempConfig.threshold_max;
                                                            let range = '';
                                                            if (min != null && max != null) range = `${min}–${max}`;
                                                            else if (min != null) range = `≥${min}`;
                                                            else if (max != null) range = `≤${max}`;
                                                            else range = '—';
                                                            return `${range} ${tempConfig.unit}`;
                                                        })()}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                Array.from({ length: profile.axle_count }, (_, i) => i + 1).map(axleNum => (
                                                    <TableRow key={axleNum}>
                                                        <TableCell>{i18n.t('vehicleDetail.alarms.axle')}{axleNum}</TableCell>
                                                        <TableCell>
                                                            {(() => {
                                                                const psiConfig = profile.configurations?.find(
                                                                    c => c.metric_type === 'PSI' && c.axle_number === axleNum
                                                                );
                                                                if (!psiConfig) return '—';
                                                                const min = psiConfig.threshold_min;
                                                                const max = psiConfig.threshold_max;
                                                                let range = '';
                                                                if (min != null && max != null) range = `${min}–${max}`;
                                                                else if (min != null) range = `≥${min}`;
                                                                else if (max != null) range = `≤${max}`;
                                                                else range = '—';
                                                                return `${range} ${psiConfig.unit}`;
                                                            })()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {(() => {
                                                                const tempConfig = profile.configurations?.find(
                                                                    c => c.metric_type === 'TEMP' && c.axle_number === axleNum
                                                                );
                                                                if (!tempConfig) return '—';
                                                                const min = tempConfig.threshold_min;
                                                                const max = tempConfig.threshold_max;
                                                                let range = '';
                                                                if (min != null && max != null) range = `${min}–${max}`;
                                                                else if (min != null) range = `≥${min}`;
                                                                else if (max != null) range = `≤${max}`;
                                                                else range = '—';
                                                                return `${range} ${tempConfig.unit}`;
                                                            })()}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>

                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('hardcoded.components_vehicles_detail_alarmprofilestab.asignado_el')}{safeFormatDateTime(assignment.assigned_at)}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
            ) : (
                <Box sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 2,
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[100], 0.5),
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    <AlarmIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('alarms.profiles.noProfilesAssigned', 'No hay perfiles asignados')}
                    </Typography>
                    {hasPermission('manager') && availableProfiles.length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<AssignIcon />}
                            onClick={() => setAssignDialogOpen(true)}
                            sx={{ mt: 2 }}
                        >
                            {t('alarms.profiles.assignProfile', 'Asignar Perfil')}
                        </Button>
                    )}
                </Box>
            )}

            <Dialog
                open={assignDialogOpen}
                onClose={() => {
                    setAssignDialogOpen(false);
                    setSelectedProfile('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t('alarms.profiles.assignProfile', 'Asignar Perfil de Alerta')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('hardcoded.components_vehicles_detail_alarmprofilestab.selecciona_un_perfil_compatible_con')}{vehicle?.axle_count} ejes)
                        </Typography>

                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>{t('navbar.profile')}</InputLabel>
                            <Select
                                value={selectedProfile}
                                onChange={(e) => setSelectedProfile(e.target.value)}
                                label={t('navbar.profile')}
                            >
                                {availableProfiles.map(profile => (
                                    <MenuItem key={profile.id} value={profile.id}>
                                        {profile.name} ({profile.axle_count} ejes, {profile.applies_to_all_axles ? t('hardcoded.components_alarmprofiles_assignmentstable.todos') : t('hardcoded.components_alarmprofiles_profilecard.por_eje')})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {availableProfiles.length === 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                {t('hardcoded.components_vehicles_detail_alarmprofilestab.no_hay_perfiles_disponibles_compatibles')}{vehicle?.axle_count} {i18n.t('hardcoded.components_alarmprofiles_assignmentdialog.ejes')}</Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => {
                            setAssignDialogOpen(false);
                            setSelectedProfile('');
                        }}
                    >
                        {t('common.cancel', 'Cancelar')}
                    </Button>
                    <Button
                        onClick={handleAssignProfile}
                        variant="contained"
                        disabled={!selectedProfile || assigning}
                        startIcon={assigning ? <CircularProgress size={20} /> : <AssignIcon />}
                    >
                        {assigning ? t('common.loading', 'Asignando...') : t('common.assign', 'Asignar')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AlarmProfilesTab;